/**
 * profileBlockPrefs — which Profile blocks are open (2026-09-19 owner ask).
 *
 * Every titled block on the Profile spine is collapsible so a trainer can
 * scroll straight to the parts they value and open those. The choice is
 * REMEMBERED per block on this device: a block collapsed today stays
 * collapsed on the next open until the trainer opens it, and vice versa.
 *
 * Defaults, by owner direction: Shiny Hunt and the Catch radar open; every
 * other block collapsed. Quick update (the post-event catch-up prompt) is
 * open too: it only exists while there is something to tick off and vanishes
 * on its own, so a collapsed prompt would be a heading with no point.
 *
 * Keys are the blocks' data-testids (stable, unlike titles such as the
 * radar's, which follows the questionnaire). InfoBlock reads its own key
 * from the collapse context; Profile's "collapse all / expand all" pill
 * acts on every key that is currently mounted.
 */
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "nexus.profileBlocks.v1";
export const PROFILE_BLOCKS_EVENT = "nexus:profile-blocks-changed";

/** Blocks that start OPEN on a fresh device. Everything else starts closed. */
export const DEFAULT_OPEN_BLOCKS: ReadonlySet<string> = new Set([
  // The GO Wild Area weekend card (2026-10-19): the point of a card that
  // only exists for two days is to be read, so it opens open.
  "profile-wild-area",
  "profile-shiny-hunt",
  // The Catch radar (EventRadarBlock in ProfilePriorityWidgets) carries the
  // legacy testid "profile-raid-block".
  "profile-raid-block",
  "section-event-catch-up",
]);

type Stored = Record<string, "open" | "closed">;

let cache: Stored | null = null;

function read(): Stored {
  if (cache) return cache;
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as unknown;
    cache = parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    cache = {};
  }
  return cache;
}

function write(next: Stored): void {
  cache = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked: the choice holds for this session via the cache */
  }
  try {
    window.dispatchEvent(new Event(PROFILE_BLOCKS_EVENT));
  } catch {
    /* SSR / tests without a window */
  }
}

export function isProfileBlockOpen(key: string): boolean {
  const v = read()[key];
  if (v === "open") return true;
  if (v === "closed") return false;
  return DEFAULT_OPEN_BLOCKS.has(key);
}

export function setProfileBlockOpen(key: string, open: boolean): void {
  write({ ...read(), [key]: open ? "open" : "closed" });
}

/** Collapse all / expand all, over the keys the page currently shows. */
export function setProfileBlocksOpen(keys: Iterable<string>, open: boolean): void {
  const next: Stored = { ...read() };
  for (const k of keys) next[k] = open ? "open" : "closed";
  write(next);
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(PROFILE_BLOCKS_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(PROFILE_BLOCKS_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

/** The stored map, re-read on every change; consumers derive per-key state. */
export function useProfileBlockPrefs(): Stored {
  return useSyncExternalStore(subscribe, read, () => ({}));
}

/** Test seam. */
export function __resetProfileBlockPrefsForTest(): void {
  cache = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
